import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Search,
  MapPin,
  Activity,
  Clock,
  Filter,
  ExternalLink
} from 'lucide-react';
import './eventlogs.css';
import { useStatus } from '../../popup/NotificationContext';
import Loader from '../../mainframes/Loader';

/**
 * 🔥 FRONTEND-ONLY TELEMETRY RESOLVER
 * Priority:
 * 1. Event snapshot (if exists)
 * 2. currentLocation
 * 3. liveData
 * 4. last locationHistory
 */
const isValidNumber = (v) => Number.isFinite(Number(v));

const resolveTelemetry = (event, device) => {
  // 1️⃣ Event snapshot
  if (isValidNumber(event?.latitude) && isValidNumber(event?.longitude)) {
    return {
      lat: Number(event.latitude),
      lng: Number(event.longitude),
      speed: Number(event.speed) || 0,
      acc: Boolean(event.accStatus),
      source: 'event'
    };
  }

  // 2️⃣ currentLocation
  if (
    isValidNumber(device?.currentLocation?.latitude) &&
    isValidNumber(device?.currentLocation?.longitude)
  ) {
    return {
      lat: Number(device.currentLocation.latitude),
      lng: Number(device.currentLocation.longitude),
      speed: Number(device.currentLocation.speed) || 0,
      acc: Boolean(device.currentLocation.accStatus),
      source: 'currentLocation'
    };
  }

  // 3️⃣ liveData
  if (
    isValidNumber(device?.liveData?.latitude) &&
    isValidNumber(device?.liveData?.longitude)
  ) {
    return {
      lat: Number(device.liveData.latitude),
      lng: Number(device.liveData.longitude),
      speed: Number(device.liveData.speed) || 0,
      acc: Boolean(device.liveData.accStatus),
      source: 'liveData'
    };
  }

  // 4️⃣ locationHistory
  const lastHistory = device?.locationHistory?.at(-1);
  if (
    isValidNumber(lastHistory?.latitude) &&
    isValidNumber(lastHistory?.longitude)
  ) {
    return {
      lat: Number(lastHistory.latitude),
      lng: Number(lastHistory.longitude),
      speed: Number(lastHistory.speed) || 0,
      acc: Boolean(lastHistory?.statusFlags?.accStatus),
      source: 'history'
    };
  }

  return {
    lat: null,
    lng: null,
    speed: 0,
    acc: false,
    source: 'none'
  };
};


function EventLogs({
  deviceEvents,
  eventLoading,
  eventError,
  formatTimestamp12h
}) {
  const { showStatus } = useStatus();

  const [filters, setFilters] = useState({
    deviceName: '',
    imei: '',
    eventType: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [addresses, setAddresses] = useState({});

  useEffect(() => {
    if (eventError) {
      showStatus('error', 'Events Sync Error: ' + eventError);
    }
  }, [eventError, showStatus]);

  const fetchAddress = useCallback(async (lat, lng, key) => {
    if (!lat || !lng || addresses[key]) return;
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await res.json();
      setAddresses(prev => ({
        ...prev,
        [key]: data?.display_name || 'Address not found'
      }));
    } catch {
      setAddresses(prev => ({
        ...prev,
        [key]: 'Address unavailable'
      }));
    }
  }, [addresses]);

  // 🔥 FLATTEN + FIX EVENTS - Updated for eventHistory structure
const flattenedEvents = useMemo(() => {
  if (!deviceEvents || !Array.isArray(deviceEvents)) return [];

  return deviceEvents.flatMap(device => {
    // ✅ Support both 'events' and 'eventHistory'
    const events = device.events || device.eventHistory || [];
    
    // ✅ CRITICAL FIX: Ensure we have device telemetry
    const deviceData = {
      imei: device.imei,
      name: device.name || device.deviceName,
      liveData: device.liveData,
      currentLocation: device.currentLocation,
      locationHistory: device.locationHistory
    };
    
    return events.map(event => {
      const telemetry = resolveTelemetry(event, deviceData);

      return {
        ...event,
        imei: deviceData.imei || 'N/A',
        deviceName: deviceData.name || 'Unknown Device',
        liveLat: telemetry.lat,
        liveLng: telemetry.lng,
        liveSpeed: telemetry.speed,
        liveAcc: telemetry.acc,
        telemetrySource: telemetry.source
      };
    });
  });
}, [deviceEvents]);

  const filteredEvents = useMemo(() => {
    return flattenedEvents.filter(e =>
      e.deviceName.toLowerCase().includes(filters.deviceName.toLowerCase()) &&
      e.imei.toLowerCase().includes(filters.imei.toLowerCase()) &&
      (e.eventType || '').toLowerCase().includes(filters.eventType.toLowerCase())
    );
  }, [flattenedEvents, filters]);

  const getEventBadgeClass = (type = '') => {
    const t = type.toLowerCase();
    if (t.includes('sos') || t.includes('over') || t.includes('alarm') || t.includes('disconnect'))
      return 'el-badge-danger';
    if (t.includes('connected') || t.includes('ignition'))
      return 'el-badge-success';
    if (t.includes('unknown'))
      return 'el-badge-warning';
    return 'el-badge-neutral';
  };

  return (
    <section className="el-container">
      <div className="el-header">
        <div>
          <h2>Live Fleet Events</h2>
          <p className="el-subtitle">
            Real-time alerts with last known telemetry
          </p>
        </div>
        <div className="el-stat">
          <span>Total Events</span>
          <strong>{filteredEvents.length}</strong>
        </div>
      </div>

      <div className="el-controls">
        <button
          className={`el-filter-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={14} /> Filters
        </button>

        <div className="el-search-wrapper">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search Vehicle Name..."
            value={filters.deviceName}
            onChange={e =>
              setFilters({ ...filters, deviceName: e.target.value })
            }
          />
        </div>
      </div>

      <div className="el-table-card">
        {eventLoading ? (
          <Loader label="Loading events..." minHeight="260px" background="#fff" scale={0.68} />
        ) : filteredEvents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <Activity size={48} style={{ opacity: 0.3 }} />
            <h3>No Events Found</h3>
            <p>There are no events to display at this time.</p>
          </div>
        ) : (
          <table className="el-table">
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Event</th>
                <th>Speed & ACC</th>
                <th>Location</th>
                <th>Time</th>
              </tr>
            </thead>

            <tbody>
              {filteredEvents.map((event, idx) => {
                const addrKey =
                  event.liveLat && event.liveLng
                    ? `${event.liveLat}-${event.liveLng}`
                    : null;

                return (
                  <tr
                    key={`${event.imei}-${event.timestamp}-${idx}`}
                    onMouseEnter={() =>
                      addrKey &&
                      fetchAddress(event.liveLat, event.liveLng, addrKey)
                    }
                  >
                    <td>
                      <strong>{event.deviceName}</strong>
                      <div className="el-text-mono">{event.imei}</div>
                    </td>

                    <td>
                      <span
                        className={`el-badge ${getEventBadgeClass(
                          event.eventType
                        )}`}
                      >
                        {event.eventType || 'Unknown Event'}
                      </span>
                      {event.details && (
                        <div className="el-text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                          {event.details}
                        </div>
                      )}
                    </td>

                    <td>
                      <div className="el-telemetry">
                        <Activity
                          size={14}
                          className={event.liveSpeed > 0 ? 'text-green' : ''}
                        />
                        <strong>{event.liveSpeed}</strong> km/h
                        <div className={`acc-dot ${event.liveAcc ? 'on' : 'off'}`} />
                        <small>{event.liveAcc ? 'ACC ON' : 'ACC OFF'}</small>
                      </div>
                      {event.telemetrySource !== 'none' && (
                        <div className="el-text-muted" style={{ fontSize: '10px', marginTop: '2px' }}>
                          Source: {event.telemetrySource}
                        </div>
                      )}
                    </td>

                    <td>
                      {event.liveLat ? (
                        <>
                          <a
                            href={`https://www.google.com/maps?q=${event.liveLat},${event.liveLng}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none', color: '#3b82f6' }}
                          >
                            {event.liveLat.toFixed(5)}, {event.liveLng.toFixed(5)} <ExternalLink size={12} />
                          </a>
                          {event.telemetrySource !== 'event' && (
                            <div className="el-text-muted" style={{ fontSize: '11px' }}>
                              Last known location
                            </div>
                          )}
                          <div className="el-text-muted" style={{ fontSize: '11px' }}>
                            {addresses[addrKey] || 'Hover to load address'}
                          </div>
                        </>
                      ) : (
                        <span className="el-text-muted">Location unavailable</span>
                      )}
                    </td>

                    <td>
                      <Clock size={12} />{' '}
                      {formatTimestamp12h(event.timestamp)}
                      {event.priority && (
                        <div className="el-text-muted" style={{ fontSize: '10px', marginTop: '2px' }}>
                          {event.priority}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export default EventLogs;
